import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import * as actions from '../../_redux/receiptsActions';
import * as columnFormatters from './column-formatters';
import * as commonColumnFormatters from '../../../_common/column-formatters';
import {useReceiptsUIContext} from '../ReceiptsUIContext';
import useInfiniteScrollControl
  from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import {useDispatch} from 'react-redux';
import CommonGrid from '../../../_common/components/CommonGrid/CommonGrid';
import SortingColumnHeader from './SortingColumnHeader';

export function ReceiptsTable() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [tableRows, setTableRows] = useState([]);
  const columns = useMemo(() => [
    {
      accessor: 'receiptID',
      Header: intl.formatMessage({
        id: 'receipts-column-receiptID', defaultMessage: 'Receipt ID'
      })
    },
    {
      accessor: 'agentID',
      Header: intl.formatMessage({
        id: 'receipts-column-agentID', defaultMessage: 'Agent'
      })
    },
    {
      accessor: 'customer',
      Header: intl.formatMessage({
        id: 'receipts-column-customer', defaultMessage: 'Customer'
      })
    },
    {
      accessor: 'date',
      Header:
        <SortingColumnHeader
          header={'receipts-column-date'}
          defaultMessage={'Date'}
          fieldName={'date'}
        />
    },
    {
      accessor: 'sum',
      Header:
        <SortingColumnHeader
          header={'receipts-column-sum'}
          defaultMessage={'Sum'}
          fieldName={'sum'}
        />
    },
    {
      accessor: 'invoices',
      Header: intl.formatMessage({
        id: 'receipts-column-invoices', defaultMessage: 'Invoices'
      })
    },
    {
      accessor: 'invoiceCreatedAt',
      Header: intl.formatMessage({
        id: 'receipts-column-invoiceCreatedAt', defaultMessage: 'Invoice created at'
      })
    },
    {
      accessor: 'downloadReceipt',
      Header: intl.formatMessage({
        id: 'receipts-column-downloadReceipt', defaultMessage: 'Download receipt'
      })
    },
    {
      accessor: 'cancelReceipt',
      Header: intl.formatMessage({
        id: 'receipts-column-cancelReceipt', defaultMessage: 'Cancel receipt'
      })
    },
  ], []);

  const data = useMemo(() => [
    ...tableRows.map((item) => {
      return {
        receiptID: commonColumnFormatters.BoldColumnFormatter(item._id),
        agentID: [
          commonColumnFormatters.BoldColumnFormatter(item.agentId),
          commonColumnFormatters.NormalColumnFormatter(item.agentName),
        ],
        customer: [
          commonColumnFormatters.BoldColumnFormatter(item.customerID),
          commonColumnFormatters.NormalColumnFormatter(item.customerName),
        ],
        date: commonColumnFormatters.DateColumnFormatter(item.date),
        sum: commonColumnFormatters.LimitDecimalColumnFormatter(item.sum),
        invoices: commonColumnFormatters.SeveralItemsColumnFormatter(item.invoices),
        invoiceCreatedAt: commonColumnFormatters.DateColumnFormatter(item.invoiceCreatedAt),
        downloadReceipt: columnFormatters.DownloadReceiptColumnFormatter(item._id, intl, dispatch),
        cancelReceipt: columnFormatters.CancelReceiptColumnFormatter(item, intl, dispatch),
      };
    })
  ], [tableRows]);

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'receipts',
    UIContext: useReceiptsUIContext(),
    getNewEntities: actions.getReceipts,
    getNextEntities: actions.getNextReceipts,
    onEntitiesChange: entities => {
      setTableRows(entities);
    },
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (
      <div className={'spinner-container'}>
        <span className={'spinner spinner-md spinner-primary'}/>
      </div>
    ),
    style: {
      overflow: 'inherit'
    },
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      tableProps={useTable({columns, data})}
      customTableStyles={'receipts-table'}
    />
  );
}
